import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faClock, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { productsAPI } from '../services/api';

/**
 * Real-time flash deal feed for the Premium Buyer/Seller dashboards.
 *
 * Shows real PRODUCTS the admin marked down (Product.is_flash_deal), not the
 * sitewide promo campaigns under Admin -> Marketing -> Sales Campaigns
 * (those are banners with no product attached, and can't be shown or clicked
 * as a product). Calls the same GET /products?flash_deal=true endpoint used
 * by the public Homepage and Shop pages. Because the request carries the
 * viewer's auth token, the backend already resolves their premium status
 * server-side and includes deals still inside their early-access window
 * (marked is_early_access) — this component doesn't need to know anything
 * about premium tiers itself, it just renders whatever the backend decides
 * this viewer can see.
 */
const FlashDealsStrip = ({ isDarkMode = false }) => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await productsAPI.getFlashDeals();
        const data = res?.data || res;
        const list = Array.isArray(data) ? data : (data?.products || []);
        if (!cancelled) setDeals(list);
      } catch {
        if (!cancelled) setDeals([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!deals.length) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deals.length]);

  const formatCountdown = (expiresAt) => {
    const diff = new Date(expiresAt) - now;
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : '#fff';
  const cardBorder = isDarkMode ? 'rgba(34,197,94,0.18)' : 'rgba(22,163,74,0.14)';
  const textPrimary = isDarkMode ? '#f0fdf4' : '#14532d';
  const textSecondary = isDarkMode ? 'rgba(240,253,244,0.6)' : 'rgba(20,83,45,0.6)';

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2.5rem', color: textSecondary }}>
        <FontAwesomeIcon icon={faBolt} spin style={{ fontSize: '1.5rem', color: '#22C55E' }} />
        <p style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>Loading deals…</p>
      </div>
    );
  }

  if (deals.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: textSecondary }}>
        <FontAwesomeIcon icon={faBolt} style={{ fontSize: '2rem', color: '#22C55E', opacity: 0.6, marginBottom: '0.75rem' }} />
        <p style={{ fontWeight: 600, color: textPrimary, marginBottom: '0.25rem' }}>No active flash deals right now</p>
        <p style={{ fontSize: '0.85rem' }}>Check back soon — new flash deals appear here first for premium members.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
      {deals.map(product => {
        const id = product.id || product._id;
        const title = product.title || product.name || 'Product';
        const price = product.flash_deal_price || product.price || 0;
        const origPrice = product.price;
        const discount = origPrice && price && origPrice > price ? Math.round((1 - price / origPrice) * 100) : null;
        const image = product.images?.[0]?.url || (typeof product.images?.[0] === 'string' ? product.images[0] : null) || product.image;
        const expires = product.flash_deal_ends_at;
        const countdown = expires ? formatCountdown(expires) : null;

        return (
          <div
            key={id}
            onClick={() => navigate(`/product/${id}`)}
            style={{
              background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '16px',
              overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.15s ease',
              boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 16px rgba(34,197,94,0.08)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ position: 'relative', height: '110px', background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(22,163,74,0.08))' }}>
              {image
                ? <img src={image} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FontAwesomeIcon icon={faBolt} style={{ fontSize: '1.75rem', color: '#22C55E' }} /></div>}
              {discount > 0 && (
                <span style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: '#EF4444', color: '#fff', fontWeight: 800, fontSize: '0.75rem', padding: '0.25rem 0.55rem', borderRadius: '20px' }}>
                  -{discount}%
                </span>
              )}
              {product.is_early_access && (
                <span style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', background: 'linear-gradient(135deg, #F59E0B, #D97706)', color: '#fff', fontWeight: 700, fontSize: '0.65rem', padding: '0.25rem 0.5rem', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <FontAwesomeIcon icon={faHourglassHalf} /> EARLY ACCESS
                </span>
              )}
            </div>
            <div style={{ padding: '0.85rem' }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</p>
              <div style={{ marginTop: '0.3rem', display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#22C55E' }}>₦{Number(price).toLocaleString()}</span>
                {origPrice > price && (
                  <span style={{ fontSize: '0.72rem', color: textSecondary, textDecoration: 'line-through' }}>₦{Number(origPrice).toLocaleString()}</span>
                )}
              </div>
              {countdown && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#22C55E', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <FontAwesomeIcon icon={faClock} /> {countdown}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FlashDealsStrip;
